import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, animate } from 'framer-motion';
import { Shield, Zap, FileText, MousePointer2, Anchor, BarChart3, Database, CheckCircle2, Download, Eye, Activity, ShieldCheck, ArrowRight } from 'lucide-react';

// Real Components from the project
import CogScene from '../components/cog/CogScene';
import LashingScene from '../components/lashing/LashingScene';

// Counter Component for Odometer Effect
const Counter: React.FC<{ from: number; to: number; duration: number; isVisible: boolean }> = ({ from, to, duration, isVisible }) => {
  const [count, setCount] = useState(from);
  useEffect(() => {
    if (!isVisible) { setCount(from); return; }
    const controls = animate(from, to, { duration: duration, onUpdate: (value) => setCount(value), ease: "easeOut" });
    return () => controls.stop();
  }, [from, to, duration, isVisible]);
  return <span>{count.toFixed(2)}</span>;
};

const scenes = [
  { id: 'intro', type: 'brand', duration: 3000 },
  { id: 'cog-analysis', type: 'real-3d-cog', title: 'Gravity & Stability Analysis', subtitle: 'Interactive 3D tracking of tipping risks.', duration: 10000 },
  { id: 'lashing-design', type: 'real-3d-lashing', title: 'Multi-Element Securing', subtitle: 'Rotate and inspect every lashing point.', duration: 12000 },
  { id: 'grid-ui', type: 'real-grid-ui', title: 'Force Analysis Dashboard', subtitle: 'Real-time IMO compliance tracking.', duration: 10000 },
  { id: 'report-preview', type: 'pdf-preview', title: 'Audit-Ready Reporting', subtitle: 'Professional PDF reports generated in seconds.', duration: 8000 },
  { id: 'cta', type: 'cta', duration: 6000 }
];

const MarketingShowreel: React.FC = () => {
  const [currentScene, setCurrentScene] = useState(0);
  const [isStarted, setIsStarted] = useState(false);
  const [demoDimensions] = useState({ width: 2.4, height: 2.4, depth: 6.0 });
  const [demoCog, setDemoCog] = useState({ width: 3.0, length: 1.2, height: 1.2 });
  const [cameraAngle, setCameraAngle] = useState({ azimuth: 0.3, elevation: 0.3 });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Sequential Animation State
  const [rowStatus, setRowStatus] = useState<Record<string, boolean>>({ d: false, s: false, l: false, f: false });

  useEffect(() => {
    if (!isStarted) return;
    const timer = setTimeout(() => {
      if (currentScene < scenes.length - 1) setCurrentScene(prev => prev + 1);
    }, scenes[currentScene].duration);
    return () => clearTimeout(timer);
  }, [currentScene, isStarted]);

  useEffect(() => {
    if (currentScene === 3 && isStarted) {
      setRowStatus({ d: false, s: false, l: false, f: false });
      const sequence = [{ id: 'd', delay: 1500 }, { id: 's', delay: 2500 }, { id: 'l', delay: 3500 }, { id: 'f', delay: 4500 }];
      const timers = sequence.map(item => setTimeout(() => setRowStatus(prev => ({ ...prev, [item.id]: true })), item.delay));
      return () => timers.forEach(t => clearTimeout(t));
    }
  }, [currentScene, isStarted]);

  useEffect(() => {
    if (!isStarted) return;
    let frame: number;
    const animateCamera = () => {
      const time = Date.now() / 1000;
      if (currentScene === 1) {
        setCameraAngle({ azimuth: Math.sin(time * 0.5) * 0.5, elevation: 0.4 + Math.cos(time * 0.3) * 0.2 });
        setMousePos({ x: 400 + Math.sin(time * 0.5) * 100, y: 400 + Math.cos(time * 0.3) * 50 });
      } else if (currentScene === 2) {
        setCameraAngle({ azimuth: time * 0.4, elevation: 0.5 });
        setMousePos({ x: 500 + Math.cos(time * 0.4) * 200, y: 300 + Math.sin(time * 0.4) * 20 });
      }
      frame = requestAnimationFrame(animateCamera);
    };
    frame = requestAnimationFrame(animateCamera);
    return () => cancelAnimationFrame(frame);
  }, [currentScene, isStarted]);

  useEffect(() => {
    if (currentScene === 1 && isStarted) {
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        if (elapsed < 3) { setDemoCog({ width: 3.0, length: 1.2, height: 1.2 }); return; }
        const time = (elapsed - 3) / 3; 
        const drift = Math.min(1, time * 0.4); 
        setDemoCog({ length: 1.2 + Math.sin(time * 1.2) * (0.8 * drift), width: 3.0 + Math.cos(time * 0.9) * (2.2 * drift), height: 1.2 + Math.sin(time * 1.5) * (0.8 * drift) });
      }, 50);
      return () => clearInterval(interval);
    } else { setDemoCog({ width: 3.0, length: 1.2, height: 1.2 }); }
  }, [currentScene, isStarted]);

  const mockLashingDetails = {
    direct_lashings: {
      forward: [{ count: 2, type: 'CL200', color: '#f97316', position: 'above' as const, msl_tur: 'MSL 10000' }],
      backward: [{ count: 2, type: 'CL200', color: '#f97316', position: 'above' as const, msl_tur: 'MSL 10000' }],
      right: [{ count: 4, type: 'CL200', color: '#3b82f6', position: 'below' as const, msl_tur: 'MSL 5000', angle: 45 }],
      left: [{ count: 4, type: 'CL200', color: '#3b82f6', position: 'below' as const, msl_tur: 'MSL 5000', angle: 45 }]
    },
    loop_lashing: { enabled: true, count: 4, type: 'Loop Lashing', color: '#10b981', buckle: 'Buckle', msl_tur: 'MSL 2000' },
    supports: {
      forward: { count: 2, type: 'stopper' as const },
      backward: { count: 2, type: 'stopper' as const },
      right: { count: 3, type: 'wedge' as const },
      left: { count: 3, type: 'wedge' as const }
    }
  };

  if (!isStarted) {
    return (
      <div className="fixed inset-0 bg-[#0a0a0b] flex items-center justify-center z-[9999]">
        <div className="text-center p-12 rounded-[40px] bg-white/[0.02] border border-white/10 backdrop-blur-3xl shadow-2xl">
          <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mb-12 inline-block">
             <img src="/sonlogo.png" alt="Logo" className="w-48 h-48 object-contain" />
          </motion.div>
          <h1 className="text-3xl font-black text-white mb-4 uppercase tracking-[0.3em]">Lashing Calculator</h1>
          <button onClick={() => setIsStarted(true)} className="px-10 py-3.5 bg-orange-600 text-white rounded-full font-medium text-xs uppercase tracking-[0.3em] hover:bg-orange-500 transition-all duration-500 shadow-xl border border-orange-500/20">PLAY SHOWREEL</button>
        </div>
      </div>
    );
  }

  const scene = scenes[currentScene];

  return (
    <div className="fixed inset-0 w-full h-full overflow-hidden bg-[#0D111C] font-sans text-white">
      <AnimatePresence>
        {(currentScene === 1 || currentScene === 2) && (
          <motion.div className="fixed z-[999] pointer-events-none" style={{ x: mousePos.x, y: mousePos.y }} initial={{ opacity: 0 }} animate={{ opacity: 0.8 }} exit={{ opacity: 0 }}>
             <MousePointer2 size={32} className="text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        <motion.div key={scene.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }} className="relative w-full h-full flex flex-col">
          
          {/* BRAND INTRO */}
          {scene.type === 'brand' && (
            <div className="flex flex-col items-center justify-center h-full gap-10 bg-black">
               <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1 }} className="w-64 h-64 flex items-center justify-center">
                 <img src="/sonlogo.png" className="w-full h-full object-contain" />
               </motion.div>
               <div className="text-center">
                  <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="text-3xl font-black tracking-[0.4em] uppercase">Lashing Calculator</motion.h1>
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="text-xs text-white/20 tracking-[0.8em] uppercase font-light">Professional Precise Proven</motion.p>
               </div>
            </div>
          )}

          {/* HEADER */}
          {scene.type !== 'brand' && scene.type !== 'cta' && scene.type !== 'pdf-preview' && (
            <div className="absolute top-12 left-0 right-0 z-50 flex flex-col items-center text-center">
               <h2 className="text-2xl md:text-3xl font-medium mb-1 uppercase tracking-[0.4em] text-white">{scene.title}</h2>
               <p className="text-[9px] md:text-xs text-orange-500/80 font-bold uppercase tracking-[0.6em]">{scene.subtitle}</p>
            </div>
          )}

          {/* ANALYSIS SCENES */}
          {scene.type === 'real-3d-cog' && (
            <div className="w-full h-full relative">
               <CogScene dimensions={demoDimensions} cog={demoCog} theme="dark" zoom={0.8} cameraAngle={cameraAngle} />
               <motion.div className="absolute bottom-[28%] right-[30%] flex flex-col gap-3 min-w-[140px]" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                  <div className="flex flex-col items-end"><span className="text-[5px] font-black uppercase tracking-[0.4em] text-white/20 mb-0.5">M_Tipping</span><div className="text-sm font-mono font-thin text-orange-500/80 tracking-wider">{(15000 * demoCog.height).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
                  <div className="flex flex-col items-end"><span className="text-[5px] font-black uppercase tracking-[0.4em] text-white/20 mb-0.5">M_Stability</span><div className="text-sm font-mono font-thin text-green-500/80 tracking-wider">{(25000 * (1.2 - Math.abs(demoCog.length - 1.2))).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
                  <div className="flex flex-col items-end"><span className="text-[5px] font-black uppercase tracking-[0.4em] text-white/20 mb-0.5">Trans_Acc</span><div className="text-sm font-mono font-thin text-purple-400/80 tracking-wider">{(0.45 + Math.abs(demoCog.width - 3.0) * 0.1).toFixed(2)} g</div></div>
                  <div className="flex flex-col items-end pt-3 border-t border-white/10"><span className="text-[5px] font-black uppercase tracking-[0.4em] text-white/20 mb-0.5">Safety_Factor</span><div className="text-2xl font-mono font-thin text-blue-400 drop-shadow-[0_0_10px_rgba(96,165,250,0.3)]">{( (25000 * (1.2 - Math.abs(demoCog.length - 1.2))) / (15000 * demoCog.height) ).toFixed(2)}</div></div>
               </motion.div>
            </div>
          )}

          {scene.type === 'real-3d-lashing' && (
            <div className="w-full h-full relative">
               <LashingScene 
                 dimensions={demoDimensions} 
                 cog={{ width: 3.0, length: 1.2, height: 1.2 }} 
                 theme="dark" 
                 lashingDetails={mockLashingDetails} 
                 transportUnit="breakbulk" 
                 breakBulkOrientation="transverse" 
                 zoom={0.65} 
                 cameraAngle={cameraAngle} 
               />
            </div>
          )}

          {/* GRID UI */}
          {scene.type === 'real-grid-ui' && (
            <div className="flex flex-col items-center justify-center h-full p-12 bg-[#0a0c10]">
               <div className="w-full max-w-4xl rounded-[40px] border border-white/5 bg-white/[0.02] shadow-2xl overflow-hidden backdrop-blur-3xl">
                  <table className="w-full text-left">
                     <thead className="bg-white/5">
                        <tr>
                           <th className="p-8 text-[10px] font-black uppercase tracking-[0.5em] text-white/30">Element Analysis</th>
                           <th className="p-8 text-[10px] font-black uppercase tracking-[0.5em] text-white/30 text-center">Safety Factor</th>
                           <th className="p-8 text-[10px] font-black uppercase tracking-[0.5em] text-white/30 text-right">Status</th>
                        </tr>
                     </thead>
                     <tbody className="divide-y divide-white/5">
                        {[
                           { id: 'd', el: 'Direct Lashing', target: 1.42, start: 0.65 },
                           { id: 's', el: 'Stoppers', target: 1.85, start: 0.72 },
                           { id: 'l', el: 'Loop Lash', target: 1.24, start: 0.88 },
                           { id: 'f', el: 'Friction', target: 2.10, start: 0.95 }
                        ].map((row, i) => (
                           <tr key={i} className="group hover:bg-white/5 transition-colors">
                              <td className="p-8 font-medium text-white/90 text-lg tracking-wide">{row.el}</td>
                              <td className="p-8 text-center text-2xl font-mono font-thin">
                                 <motion.div animate={{ color: rowStatus[row.id] ? '#4ade80' : '#ef4444' }}>
                                    <Counter from={row.start} to={row.target} duration={1} isVisible={currentScene === 3} />
                                 </motion.div>
                              </td>
                              <td className="p-8 text-right min-w-[120px]">
                                 <AnimatePresence mode="wait">
                                    {rowStatus[row.id] ? (
                                       <motion.div key="v" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-end gap-2 text-green-500 font-black text-[10px] tracking-widest uppercase">Verified <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.5)]" /></motion.div>
                                    ) : (
                                       <motion.div key="c" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-end gap-2 text-red-500/60 font-black text-[10px] tracking-widest uppercase">Analyzing <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-ping" /></motion.div>
                                    )}
                                 </AnimatePresence>
                              </td>
                           </tr>
                        ))}
                     </tbody>
                  </table>
               </div>
            </div>
          )}

          {/* PDF PREVIEW & CTA */}
          {scene.type === 'pdf-preview' && (
            <div className="flex flex-col items-center justify-center h-full bg-[#11141d] p-6">
               <motion.div className="relative w-[850px] h-[90%] bg-white rounded-xl shadow-2xl overflow-hidden" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                  <div className="w-full h-full bg-white relative overflow-hidden">
                     <motion.img src="/promo-pdf.png" className="w-full h-auto" initial={{ y: 0 }} animate={{ y: "-60%" }} transition={{ duration: 8, delay: 1, ease: "easeInOut" }} />
                  </div>
                  <div className="absolute top-0 left-0 right-0 p-5 bg-white/95 border-b flex justify-between items-center z-20">
                     <div className="flex items-center gap-3"><img src="/sonlogo.png" className="w-8 h-8" /><span className="text-[10px] font-black uppercase text-black">Audit Report</span></div>
                  </div>
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"><div className="px-8 py-3 bg-orange-600 text-white rounded-full font-bold text-[10px] uppercase tracking-widest shadow-2xl">GENERATION COMPLETE</div></div>
               </motion.div>
            </div>
          )}

          {scene.type === 'cta' && (
            <div className="flex flex-col items-center justify-center h-full text-center bg-black">
               <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                  <img src="/sonlogo.png" alt="Logo" className="w-64 h-64 object-contain mx-auto mb-12" />
                  <h2 className="text-4xl font-black mb-6 uppercase text-white tracking-[0.4em]">Lashing Calculator</h2>
                  <p className="text-xl font-bold text-orange-500 tracking-[0.6em] mb-16 uppercase">The Smartest Cargo Tool</p>
                  <button className="px-10 py-3.5 bg-white text-black rounded-full font-medium text-xs uppercase tracking-[0.3em]">Get Started</button>
               </motion.div>
            </div>
          )}

          <div className="absolute bottom-0 left-0 h-1.5 bg-white/5 w-full z-50">
            <motion.div key={currentScene} className="h-full bg-orange-600" initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: scene.duration / 1000, ease: 'linear' }} />
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default MarketingShowreel;
